import { apiClient } from './client.js';
import type { Gym } from '@atomfit/shared';
import type { CreateGymInput, UpdateGymInput } from '@atomfit/shared';
import type { PaginatedResult } from '../types/index.js';

export interface LockDeviceStatus {
  smartlockId: number;
  name: string | null;
  online: boolean | null;
  locked: boolean | null;
  doorOpen: boolean | null;
  lockState: string | null;
  doorState: string | null;
  batteryCritical: boolean | null;
  batteryCharging: boolean | null;
  batteryCharge: number | null;
  keypadBatteryCritical: boolean | null;
  error: string | null;
}

function tenantHeaders(tenantSlug?: string) {
  return tenantSlug ? { headers: { 'X-Tenant-ID': tenantSlug } } : undefined;
}

export const gymsApi = {
  list: (page = 1) => apiClient.get<PaginatedResult<Gym>>('/gyms', { params: { page } }),

  get: (gymId: string) => apiClient.get<Gym>(`/gyms/${gymId}`),

  adminList: (role: string, page = 1) =>
    role === 'PLATFORM_ADMIN'
      ? apiClient.get<PaginatedResult<Gym>>('/admin/gyms', { params: { page } })
      : apiClient.get<PaginatedResult<Gym>>('/owner/gyms', { params: { page } }),

  create: (data: CreateGymInput, tenantSlug?: string) =>
    apiClient.post<Gym>('/gyms', data, tenantHeaders(tenantSlug)),

  update: (gymId: string, data: UpdateGymInput, tenantSlug?: string) =>
    apiClient.put<Gym>(`/gyms/${gymId}`, data, tenantHeaders(tenantSlug)),

  delete: (gymId: string, tenantSlug?: string) =>
    apiClient.delete(`/gyms/${gymId}`, tenantHeaders(tenantSlug)),

  lockStatuses: (gymId: string, tenantSlug?: string) =>
    apiClient.get<{ data: LockDeviceStatus[] }>(
      `/owner/gyms/${gymId}/locks`,
      tenantHeaders(tenantSlug),
    ),

  lockAction: (
    gymId: string,
    smartlockId: number,
    action: 'lock' | 'unlock' | 'unlatch',
    tenantSlug?: string,
  ) =>
    apiClient.post<{ success: true }>(
      `/owner/gyms/${gymId}/locks/${smartlockId}/${action}`,
      {},
      tenantHeaders(tenantSlug),
    ),
};
